import { redirect, type MiddlewareFunction } from "react-router";

import { localStorage } from "shared/utils";
import { LocalStorageKey, Permission } from "shared/types";
import { evaluateAuthToken } from "./auth-status";
import { LegacyAuthDeniedError } from "./legacy-auth-denied";
import { to } from "../to";

const denyLegacyAccess = (): never => {
  localStorage.removeItem(LocalStorageKey.TOKEN);
  throw new LegacyAuthDeniedError();
};

const requireModernPermission = (requiredPermission: Permission): MiddlewareFunction => {
  return async (_args, next) => {
    const snapshot = evaluateAuthToken(localStorage.getItem(LocalStorageKey.TOKEN));

    if (snapshot.status === "anonymous" || snapshot.status === "expired") {
      localStorage.removeItem(LocalStorageKey.TOKEN);
      throw redirect(to.login);
    }

    if (snapshot.status === "invalid") {
      throw snapshot.error;
    }

    if (requiredPermission < snapshot.permission) {
      throw redirect(to.root);
    }

    return next();
  };
};

const requireLegacyPermission = (requiredPermission: number): MiddlewareFunction => {
  return async (_args, next) => {
    const snapshot = evaluateAuthToken(localStorage.getItem(LocalStorageKey.TOKEN));

    if (snapshot.status === "invalid") {
      throw snapshot.error;
    }

    if (
      snapshot.status === "anonymous" ||
      snapshot.status === "expired" ||
      requiredPermission < snapshot.permission
    ) {
      denyLegacyAccess();
    }

    return next();
  };
};

// Note: requireAuth historically used 4 for user
const requireLegacyUser = requireLegacyPermission(Permission.USER);
const requireLegacySales = requireLegacyPermission(Permission.SALES);
const requireLegacyAdmin = requireLegacyPermission(Permission.ADMIN);

export { requireLegacyAdmin, requireLegacySales, requireLegacyUser, requireModernPermission };
